"use client";

import { useEffect, useState } from "react";
import {
  X,
  Star,
  MapPin,
  Music,
  Award,
  CheckCircle,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../ui/Button";
import { motion, AnimatePresence } from "framer-motion";
import { formatLessonPriceLabel } from "@/lib/lesson/lessonPricing";
import {
  getLessonKakaoInquiryMessage,
  isLessonConsultationOnly,
  LESSON_KAKAO_CHANNEL_HANDLE,
  openLessonKakaoInquiry,
} from "@/lib/config/lessonBooking";
import LessonCalendarPicker from "@/components/scheduling/LessonCalendarPicker";
import type { SlotOption } from "@/lib/scheduling/types";
import toast from "react-hot-toast";

interface LessonType {
  id: string;
  name: string;
  type: "single" | "monthly" | "pro";
  duration: number;
  price?: number | null;
  pricePerSession?: number;
  priceNegotiable?: boolean;
  monthlyOptions?: {
    sessions: number;
    discount: number;
    label: string;
  }[];
  description: string;
}

interface Instructor {
  id: string;
  name: string;
  photo: string;
  specialty: string;
  instruments: string[];
  pricePerHour: number;
  rating: number;
  reviewCount: number;
  location: string;
  experience: string;
  bio: string;
  tagline?: string;
  certifications: string[];
  tags: string[];
  availability?: string[];
  lessonTypes?: LessonType[];
  reviews?: Array<{
    userName: string;
    rating: number;
    comment: string;
    date: string;
    verified?: boolean;
    beta?: boolean;
  }>;
}

interface InstructorModalProps {
  instructor: Instructor | null;
  isOpen: boolean;
  onClose: () => void;
  onBookLesson: (
    instructor: Instructor,
    lessonType: LessonType,
    slot: SlotOption
  ) => void;
}

export function InstructorModal({
  instructor,
  isOpen,
  onClose,
  onBookLesson,
}: InstructorModalProps) {
  const [selectedLessonType, setSelectedLessonType] = useState(0);
  const [selectedMonthlyOption, setSelectedMonthlyOption] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<SlotOption | null>(null);

  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedLessonType, instructor?.id]);

  if (!instructor) return null;

  const lessonTypes: LessonType[] =
    instructor.lessonTypes && instructor.lessonTypes.length > 0
      ? instructor.lessonTypes
      : [
          {
            id: "single",
            name: "1:1 개인 레슨",
            type: "single",
            duration: 60,
            price: instructor.pricePerHour > 0 ? instructor.pricePerHour : null,
            priceNegotiable: !(instructor.pricePerHour > 0),
            description: "커넥톤에서 결제 후 일정을 조율하는 1:1 레슨",
          },
        ];

  const selectedLesson = lessonTypes[selectedLessonType] ?? lessonTypes[0];
  const durationMinutes = selectedLesson?.duration || 60;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between z-10">
                <div className="flex items-start space-x-4 flex-1">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={instructor.photo}
                    alt={instructor.name}
                    className="w-20 h-20 rounded-full object-cover object-top"
                  />
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      {instructor.name}
                    </h2>
                    <p className="text-gray-600 mb-2">
                      {instructor.tagline || instructor.specialty}
                    </p>
                    <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                      <div className="flex items-center space-x-1 bg-yellow-50 px-3 py-1 rounded-lg">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="font-bold text-gray-900">
                          {instructor.reviewCount > 0
                            ? instructor.rating.toFixed(1)
                            : "커넥톤 공식 프로"}
                        </span>
                        {instructor.reviewCount > 0 && (
                          <span className="text-sm text-gray-500">
                            ({instructor.reviewCount})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 text-gray-600 text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>{instructor.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        담당 악기
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {instructor.instruments.map((instrument, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center space-x-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            <Music className="w-4 h-4" />
                            <span>{instrument}</span>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        소개
                      </h3>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {instructor.bio}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                        <Award className="w-5 h-5 text-blue-600" />
                        <span>경력</span>
                      </h3>
                      {instructor.certifications.length > 0 ? (
                        <ul className="space-y-2">
                          {instructor.certifications.map((cert, idx) => (
                            <li
                              key={idx}
                              className="flex items-start space-x-2 text-gray-700"
                            >
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{cert}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 whitespace-pre-wrap">
                          {instructor.experience}
                        </p>
                      )}
                    </div>

                    {!isLessonConsultationOnly() && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">
                          레슨 가능 시간 ({durationMinutes}분 기준)
                        </h3>
                        <LessonCalendarPicker
                          instructorId={instructor.id}
                          durationMinutes={durationMinutes}
                          selectedSlot={selectedSlot}
                          onSelect={setSelectedSlot}
                          theme="light"
                        />
                      </div>
                    )}

                    {instructor.reviews && instructor.reviews.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">
                          후기
                        </h3>
                        <div className="space-y-3">
                          {instructor.reviews.slice(0, 5).map((review, idx) => (
                            <div
                              key={idx}
                              className="rounded-xl border border-gray-100 bg-gray-50 p-4"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  {review.userName}
                                </span>
                                <span className="text-yellow-500 text-sm">
                                  {"★".repeat(review.rating)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">
                                {review.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-1">
                    <div className="sticky top-28 rounded-2xl border border-gray-200 p-5 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        레슨 선택
                      </h3>
                      <div className="space-y-3 mb-5">
                        {lessonTypes.map((lesson, idx) => (
                          <button
                            key={lesson.id || idx}
                            type="button"
                            onClick={() => setSelectedLessonType(idx)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                              selectedLessonType === idx
                                ? "border-blue-600 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="font-semibold text-gray-900 mb-1">
                              {lesson.name}
                            </div>
                            <div className="text-sm text-gray-600 mb-1">
                              {lesson.duration}분
                            </div>
                            <div className="text-base font-bold text-blue-600">
                              {formatLessonPriceLabel(lesson)}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {lesson.description}
                            </p>
                          </button>
                        ))}
                      </div>

                      {selectedLesson?.type === "monthly" &&
                        selectedLesson.monthlyOptions &&
                        !isLessonConsultationOnly() && (
                          <div className="mb-5">
                            <label className="text-sm font-medium text-gray-700 mb-3 block">
                              희망 레슨 횟수
                            </label>
                            <div className="space-y-3">
                              {selectedLesson.monthlyOptions.map(
                                (option, idx) => {
                                  const pricePerSession =
                                    selectedLesson.pricePerSession || 0;
                                  const discountedPrice =
                                    pricePerSession *
                                    (1 - option.discount / 100);
                                  const totalPrice =
                                    discountedPrice * option.sessions;

                                  return (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() =>
                                        setSelectedMonthlyOption(idx)
                                      }
                                      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                                        selectedMonthlyOption === idx
                                          ? "border-blue-600 bg-blue-50"
                                          : "border-gray-200 hover:border-gray-300"
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-3 mb-2">
                                        <span className="font-semibold text-gray-900 text-base leading-tight">
                                          {option.label}
                                        </span>
                                        {option.discount > 0 && (
                                          <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-bold flex-shrink-0">
                                            {option.discount}% 할인
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-baseline gap-2 flex-wrap">
                                        {option.discount > 0 && (
                                          <span className="text-sm text-gray-400 line-through">
                                            {(
                                              pricePerSession * option.sessions
                                            ).toLocaleString()}
                                            원
                                          </span>
                                        )}
                                        <span className="text-base font-bold text-blue-600">
                                          {totalPrice.toLocaleString()}원
                                        </span>
                                      </div>
                                    </button>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}

                      <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/80 p-3">
                        <div className="flex items-start gap-2">
                          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <p className="text-xs leading-5 text-blue-900">
                            {isLessonConsultationOnly()
                              ? `베타 테스터는 레슨 일정·요금 상담을 카카오톡 ${LESSON_KAKAO_CHANNEL_HANDLE} 로 문의해 주세요.`
                              : selectedSlot
                                ? `${selectedSlot.date} ${selectedSlot.time} 선택됨 · 결제 완료 시 즉시 확정됩니다.`
                                : "캘린더에서 날짜와 시간을 선택한 뒤 신청해 주세요."}
                          </p>
                        </div>
                      </div>

                      <Button
                        className="w-full"
                        size="lg"
                        onClick={() => {
                          if (isLessonConsultationOnly()) {
                            toast(getLessonKakaoInquiryMessage(), {
                              icon: "💬",
                              duration: 6000,
                            });
                            openLessonKakaoInquiry();
                            onClose();
                            return;
                          }
                          if (!selectedSlot) {
                            toast.error("레슨 날짜와 시간을 선택해 주세요.");
                            return;
                          }
                          onBookLesson(instructor, selectedLesson, selectedSlot);
                          onClose();
                        }}
                      >
                        {isLessonConsultationOnly()
                          ? "카카오톡 상담 문의"
                          : "선택 시간으로 결제하기"}
                      </Button>

                      <p className="text-xs text-gray-500 text-center mt-3">
                        {isLessonConsultationOnly()
                          ? `${LESSON_KAKAO_CHANNEL_HANDLE} 채널로 문의해 주세요`
                          : "결제 완료 시 예약이 바로 확정됩니다"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
